/**
 * Refills Slice
 * Redux state management for refill tracking
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import RefillService from '../services/refillService';

// Async thunks
export const fetchRefills = createAsyncThunk(
    'refills/fetchRefills',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await RefillService.searchRefills(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch refills');
        }
    }
);

export const fetchRefillMetrics = createAsyncThunk(
    'refills/fetchMetrics',
    async (_, { rejectWithValue }) => {
        try {
            const response = await RefillService.getMetrics();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch metrics');
        }
    }
);

export const updateRefillStatus = createAsyncThunk(
    'refills/updateStatus',
    async ({ refillId, update }, { rejectWithValue, dispatch }) => {
        try {
            await RefillService.updateStatus(refillId, update);
            // Refresh the list after update
            dispatch(fetchRefills({}));
            return { refillId, update };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update status');
        }
    }
);

export const logRefillCommunication = createAsyncThunk(
    'refills/logCommunication',
    async ({ refillId, logData }, { rejectWithValue }) => {
        try {
            const response = await RefillService.logCommunication(refillId, logData);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to log communication');
        }
    }
);

export const resolveRefill = createAsyncThunk(
    'refills/resolve',
    async ({ refillId, reason, notes }, { rejectWithValue, dispatch }) => {
        try {
            await RefillService.resolveRefill(refillId, reason, notes);
            dispatch(fetchRefills({}));
            dispatch(fetchRefillMetrics());
            return { refillId };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to resolve refill');
        }
    }
);

const initialState = {
    refills: [],
    metrics: {
        totalPending: 0,
        highPriority: 0,
        mediumPriority: 0,
        lowPriority: 0,
        providersNotResponded: 0,
        autoRefillCount: 0
    },
    loading: false,
    metricsLoading: false,
    error: null,
    filters: {
        page: 1,
        limit: 20,
        status: [],
        sortBy: 'daysPending',
        sortOrder: 'desc'
    },
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    }
};

const refillsSlice = createSlice({
    name: 'refills',
    initialState,
    reducers: {
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = { ...initialState.filters };
        },
        clearError: (state) => {
            state.error = null;
        },
        setPage: (state, action) => {
            state.filters.page = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch refills
            .addCase(fetchRefills.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRefills.fulfilled, (state, action) => {
                state.loading = false;
                state.refills = action.payload.data || [];
                state.pagination = action.payload.pagination || state.pagination;
            })
            .addCase(fetchRefills.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch metrics
            .addCase(fetchRefillMetrics.pending, (state) => {
                state.metricsLoading = true;
            })
            .addCase(fetchRefillMetrics.fulfilled, (state, action) => {
                state.metricsLoading = false;
                state.metrics = action.payload || state.metrics;
            })
            .addCase(fetchRefillMetrics.rejected, (state, action) => {
                state.metricsLoading = false;
                state.error = action.payload;
            })
            // Update status
            .addCase(updateRefillStatus.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Log communication
            .addCase(logRefillCommunication.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Resolve refill
            .addCase(resolveRefill.rejected, (state, action) => {
                state.error = action.payload;
            });
    }
});

export const { setFilters, clearFilters, clearError, setPage } = refillsSlice.actions;
export default refillsSlice.reducer;
